import { LOCALE_TO_LANG, type Lang, type Locale } from "../config/langs";
import type { CardList } from "../config/lists";
import type { Card, Direction } from "../types";
import { splitAgreements } from "./text";

/**
 * Resolve a face key to a language code suitable for TTS / voice
 * lookups. Bilingual decks store the Lang on each face; mono decks
 * don't (both faces use the UI locale), so fall back to the locale's
 * canonical Lang. Centralising this avoids scattering shape checks
 * across callers that just want "the lang for this face".
 */
export function faceLang(
  faceKey: string,
  list: CardList,
  locale: Locale,
): Lang {
  const face = list.faces.find((f) => f.key === faceKey);
  if (face?.lang) return face.lang;
  return LOCALE_TO_LANG[locale];
}

// Pick a direction for the next card. Returns {from, to} lang codes.
// dirRatio = % of cards where the prompt is in the KNOWN (native) lang
// (= you must produce the target lang). 0 = always read target / write
// native (recognition mode), 100 = always read native / write target
// (production mode), 50 = mix.
export function pickDirection(
  known: Direction["from"],
  target: Direction["to"],
  dirRatio: number,
): Direction {
  const writeTarget = Math.random() * 100 < dirRatio;
  return writeTarget ? { from: known, to: target } : { from: target, to: known };
}

/**
 * Get the prompt text to display on the card.
 * For adjectives with agreements (";"), show only the masculine (base)
 * form when prompting from that language.
 */
export function getPromptText(card: Card, dir: Direction): string {
  const raw = card.faces[dir.from] || "";
  const info = splitAgreements(raw);
  return info.hasAgreements ? info.base : raw;
}

/**
 * Get the expected answer text for validation.
 * For adjectives with agreements, accept only the base (masculine) form.
 */
export function getExpectedText(card: Card, dir: Direction): string {
  const raw = card.faces[dir.to] || "";
  const info = splitAgreements(raw);
  return info.hasAgreements ? info.base : raw;
}
